/**
 * @fileoverview System-web local-first service for Customer Portal
 *               configuration — system defaults + per-tenant config.
 * @module system-web/services/portalConfigService
 *
 * Responsibilities:
 * - Dexie reads for the cached portal config (api_usage_cache — the same
 *   "online fetch → Dexie → liveQuery" shape tenant-web uses, since
 *   customerPortalConfigs lives in the system DB but is not sync-mapped).
 * - Save paths via the shared `api` wrapper (never a component call).
 *
 * Dependencies: ../apiClient (api portalConfig wrapper), @firesquirrel/local-db.
 * Architecture: system-DB document, read-through local cache; writes go
 *   through the system-api /api/customer-portal/* boundary.
 *
 * @localFirst
 * Source of Truth: server (system DB customerPortalConfigs).
 * Write Flow:    apiFetch → api_usage_cache put; liveQuery re-emits.
 * Sync Strategy: on-demand fetch on mount + write-through on save.
 * Conflict Resolution: server wins — cache always overwritten.
 */

import { getSystemDb, peekSystemDb } from '@firesquirrel/local-db';
import { apiFetch } from '../apiClient';
import type { PortalConfigValue } from '@firesquirrel/ui-admin';

const DEFAULTS_CACHE_KEY = 'portal-config:defaults';
const tenantCacheKey = (tenantId: string) => `portal-config:tenant:${tenantId}`;

export interface PortalConfigBundle {
  config: PortalConfigValue & { paymentGateway?: Record<string, unknown> };
  fetchedAt: string;
}

/** Pure Dexie read — liveQuery-safe. */
export async function getCachedPortalDefaults(): Promise<PortalConfigBundle | null> {
  const db = peekSystemDb() ?? await getSystemDb();
  return (await db.api_usage_cache.get(DEFAULTS_CACHE_KEY)) as unknown as PortalConfigBundle | null;
}

/** Pure Dexie read for one tenant's config — liveQuery-safe. */
export async function getCachedTenantPortalConfig(tenantId: string): Promise<PortalConfigBundle | null> {
  const db = peekSystemDb() ?? await getSystemDb();
  return (await db.api_usage_cache.get(tenantCacheKey(tenantId))) as unknown as PortalConfigBundle | null;
}

/**
 * @process refreshPortalDefaults
 * Steps: GET /api/customer-portal/defaults → write the cache row.
 * Idempotency: REQUIRED — the row is fully overwritten.
 */
export async function refreshPortalDefaults(): Promise<void> {
  const res = await apiFetch('/api/customer-portal/defaults');
  const db = peekSystemDb() ?? await getSystemDb();
  await db.api_usage_cache.put({
    _id: DEFAULTS_CACHE_KEY,
    config: res.config,
    fetchedAt: new Date().toISOString(),
  } as any);
}

export async function refreshTenantPortalConfig(tenantId: string): Promise<void> {
  const res = await apiFetch(`/api/customer-portal/${encodeURIComponent(tenantId)}/config`);
  const db = peekSystemDb() ?? await getSystemDb();
  await db.api_usage_cache.put({
    _id: tenantCacheKey(tenantId),
    config: res.config,
    fetchedAt: new Date().toISOString(),
  } as any);
}

/**
 * @process savePortalDefaults / saveTenantPortalConfig
 * Steps: PUT → write-through the returned config into the cache row.
 * Idempotency: REQUIRED — PUT replaces writable fields.
 */
export async function savePortalDefaults(patch: PortalConfigValue): Promise<PortalConfigValue> {
  const res = await apiFetch('/api/customer-portal/defaults', {
    method: 'PUT', body: JSON.stringify(patch),
  });
  const db = peekSystemDb() ?? await getSystemDb();
  await db.api_usage_cache.put({
    _id: DEFAULTS_CACHE_KEY,
    config: res.config,
    fetchedAt: new Date().toISOString(),
  } as any);
  return res.config;
}

export async function saveTenantPortalConfig(tenantId: string, patch: PortalConfigValue): Promise<PortalConfigValue> {
  const res = await apiFetch(`/api/customer-portal/${encodeURIComponent(tenantId)}/config`, {
    method: 'PUT', body: JSON.stringify(patch),
  });
  const db = peekSystemDb() ?? await getSystemDb();
  await db.api_usage_cache.put({
    _id: tenantCacheKey(tenantId),
    config: res.config,
    fetchedAt: new Date().toISOString(),
  } as any);
  return res.config;
}
